import { Controller } from "@hotwired/stimulus"
import "@github/markdown-toolbar-element"
import { subscribe as subscribePasteMarkdown } from "@github/paste-markdown"
import autosize from "@github/textarea-autosize"

export default class extends Controller {
  static targets = [
    "textarea", "writePane", "previewPane", "tabWrite", "tabPreview",
    "fileInput", "draftNotice", "draftNoticeText"
  ]
  static values  = { previewUrl: String, uploadUrl: String }
  static classes = ["activeTab", "inactiveTab", "dragOver"]

  // Bump if the stored draft schema ever changes.
  DRAFT_SCHEMA = 1
  DRAFT_DEBOUNCE_MS = 500

  connect() {
    this.pasteMarkdownSub = subscribePasteMarkdown(this.textareaTarget)
    this.autosizeSub      = autosize(this.textareaTarget)

    this.boundDragOver    = this.onDragOver.bind(this)
    this.boundDragLeave   = this.onDragLeave.bind(this)
    this.boundDrop        = this.onDrop.bind(this)
    this.boundPaste       = this.onPaste.bind(this)
    this.boundAutosave    = this.queueAutosave.bind(this)
    this.boundClearOnSubmit = this.clearDraft.bind(this)

    this.textareaTarget.addEventListener("dragover",  this.boundDragOver)
    this.textareaTarget.addEventListener("dragleave", this.boundDragLeave)
    this.textareaTarget.addEventListener("drop",      this.boundDrop)
    this.textareaTarget.addEventListener("paste",     this.boundPaste)
    this.textareaTarget.addEventListener("input",     this.boundAutosave)

    this.form = this.element.closest("form")
    if (this.form) this.form.addEventListener("submit", this.boundClearOnSubmit)

    this.maybeShowDraftNotice()
  }

  disconnect() {
    this.pasteMarkdownSub?.unsubscribe?.()
    this.autosizeSub?.unsubscribe?.()
    clearTimeout(this.autosaveTimer)

    this.textareaTarget.removeEventListener("dragover",  this.boundDragOver)
    this.textareaTarget.removeEventListener("dragleave", this.boundDragLeave)
    this.textareaTarget.removeEventListener("drop",      this.boundDrop)
    this.textareaTarget.removeEventListener("paste",     this.boundPaste)
    this.textareaTarget.removeEventListener("input",     this.boundAutosave)

    if (this.form) this.form.removeEventListener("submit", this.boundClearOnSubmit)
  }

  // ---- Tab switching --------------------------------------------------

  showWrite(event) {
    event?.preventDefault()
    this.writePaneTarget.hidden   = false
    this.previewPaneTarget.hidden = true
    this.setActiveTab(this.tabWriteTarget, this.tabPreviewTarget)
  }

  async showPreview(event) {
    event?.preventDefault()

    // Lock min-height to match the textarea so the layout doesn't jump
    // while preview is loading.
    const height = this.textareaTarget.getBoundingClientRect().height
    this.previewPaneTarget.style.minHeight = `${Math.max(height, 80)}px`
    this.previewPaneTarget.innerHTML = '<div class="md-editor__preview-loading">Loading preview…</div>'

    this.writePaneTarget.hidden   = true
    this.previewPaneTarget.hidden = false
    this.setActiveTab(this.tabPreviewTarget, this.tabWriteTarget)

    try {
      const res = await fetch(this.previewUrlValue, {
        method:  "POST",
        headers: this.jsonHeaders(),
        body:    JSON.stringify({ text: this.textareaTarget.value })
      })

      if (!res.ok) throw new Error(`Preview failed: ${res.status}`)

      const data = await res.json()
      this.previewPaneTarget.innerHTML = data.html || '<div class="md-editor__preview-empty">Nothing to preview.</div>'
    } catch (err) {
      this.previewPaneTarget.innerHTML = `<div class="md-editor__preview-error">${err.message}</div>`
    }
  }

  setActiveTab(active, inactive) {
    if (this.hasActiveTabClass) {
      active.classList.add(...this.activeTabClasses)
      inactive.classList.remove(...this.activeTabClasses)
    }
    if (this.hasInactiveTabClass) {
      inactive.classList.add(...this.inactiveTabClasses)
      active.classList.remove(...this.inactiveTabClasses)
    }
    active.setAttribute("aria-selected", "true")
    inactive.setAttribute("aria-selected", "false")
  }

  // ---- Browse-button upload -------------------------------------------

  browseForImage(event) {
    event?.preventDefault()
    if (this.hasFileInputTarget) this.fileInputTarget.click()
  }

  onFilesSelected(event) {
    const input = event.target
    for (const file of input.files) {
      if (file.type.startsWith("image/")) this.uploadImage(file)
    }
    // Reset so picking the same file twice in a row still fires `change`.
    input.value = ""
  }

  // ---- Drag-drop + paste image upload ---------------------------------

  onDragOver(event) {
    if (this.eventHasFiles(event)) {
      event.preventDefault()
      if (this.hasDragOverClass) this.element.classList.add(...this.dragOverClasses)
    }
  }

  onDragLeave() {
    if (this.hasDragOverClass) this.element.classList.remove(...this.dragOverClasses)
  }

  onDrop(event) {
    if (!this.eventHasFiles(event)) return
    event.preventDefault()
    if (this.hasDragOverClass) this.element.classList.remove(...this.dragOverClasses)

    for (const file of event.dataTransfer.files) {
      if (file.type.startsWith("image/")) this.uploadImage(file)
    }
  }

  onPaste(event) {
    if (!event.clipboardData) return
    const images = Array.from(event.clipboardData.files || []).filter(f => f.type.startsWith("image/"))
    if (images.length === 0) return

    // We're handling images ourselves; @github/paste-markdown still handles
    // non-image rich-text paste because we let those events through.
    event.preventDefault()
    for (const file of images) this.uploadImage(file)
  }

  async uploadImage(file) {
    const token       = this.uploadToken()
    const placeholder = `![uploading ${file.name}…](#${token})`

    this.insertAtCursor(placeholder)

    const form = new FormData()
    form.append("file", file)

    try {
      const res = await fetch(this.uploadUrlValue, {
        method:  "POST",
        headers: { "Accept": "application/json", "X-CSRF-Token": this.csrfToken() },
        body:    form
      })

      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        throw new Error(body.error || `Upload failed: ${res.status}`)
      }

      const data = await res.json()
      this.replaceInTextarea(placeholder, `[image:${data.slug}]`)
    } catch (err) {
      this.replaceInTextarea(placeholder, `<!-- upload failed: ${err.message} -->`)
    }
  }

  // ---- Autosave to localStorage ---------------------------------------
  //
  // Keyed by window.location.pathname + textarea id so /type/article/new
  // and /type/article/<slug>/edit get distinct drafts, and each long-
  // text field on a given form gets its own slot. Stored value is
  // { schema, text, savedAt } JSON. Saved debounced on input; restore is
  // explicit via a notice strip (no silent replacement); cleared on
  // form submit.

  draftKey() {
    return `md-draft:${window.location.pathname}:${this.textareaTarget.id}`
  }

  loadDraft() {
    try {
      const raw = localStorage.getItem(this.draftKey())
      if (!raw) return null
      const data = JSON.parse(raw)
      if (data?.schema !== this.DRAFT_SCHEMA) return null
      return data
    } catch (_) {
      return null
    }
  }

  saveDraft() {
    try {
      const payload = JSON.stringify({
        schema:  this.DRAFT_SCHEMA,
        text:    this.textareaTarget.value,
        savedAt: Date.now()
      })
      localStorage.setItem(this.draftKey(), payload)
    } catch (_) {
      // Quota exceeded / disabled storage / private browsing — ignore.
    }
  }

  queueAutosave() {
    clearTimeout(this.autosaveTimer)
    this.autosaveTimer = setTimeout(() => this.saveDraft(), this.DRAFT_DEBOUNCE_MS)
  }

  clearDraft() {
    try { localStorage.removeItem(this.draftKey()) } catch (_) {}
  }

  maybeShowDraftNotice() {
    const draft = this.loadDraft()
    if (!draft) return
    if (typeof draft.text !== "string") return
    if (draft.text === this.textareaTarget.value) return

    if (this.hasDraftNoticeTextTarget) {
      this.draftNoticeTextTarget.textContent = `Unsaved draft from ${this.formatTimeAgo(draft.savedAt)}.`
    }
    if (this.hasDraftNoticeTarget) this.draftNoticeTarget.hidden = false
  }

  restoreDraft(event) {
    event?.preventDefault()
    const draft = this.loadDraft()
    if (!draft || typeof draft.text !== "string") return

    // Replace the textarea contents via execCommand so the restore
    // itself participates in the native undo stack — user can Cmd+Z
    // back to whatever the form was loaded with.
    this.textareaTarget.focus()
    this.textareaTarget.setSelectionRange(0, this.textareaTarget.value.length)
    const replaced = document.execCommand("insertText", false, draft.text)
    if (!replaced) {
      this.textareaTarget.value = draft.text
      this.textareaTarget.dispatchEvent(new Event("input", { bubbles: true }))
    }

    if (this.hasDraftNoticeTarget) this.draftNoticeTarget.hidden = true
  }

  discardDraft(event) {
    event?.preventDefault()
    this.clearDraft()
    if (this.hasDraftNoticeTarget) this.draftNoticeTarget.hidden = true
  }

  formatTimeAgo(timestamp) {
    if (!timestamp) return "earlier"
    const seconds = Math.max(1, Math.floor((Date.now() - timestamp) / 1000))
    const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" })
    if (seconds < 60)      return rtf.format(-seconds, "second")
    if (seconds < 3600)    return rtf.format(-Math.floor(seconds / 60),   "minute")
    if (seconds < 86_400)  return rtf.format(-Math.floor(seconds / 3600), "hour")
    return rtf.format(-Math.floor(seconds / 86_400), "day")
  }

  // ---- Undo / Redo ----------------------------------------------------

  undo(event) {
    event?.preventDefault()
    this.textareaTarget.focus()
    document.execCommand("undo")
  }

  redo(event) {
    event?.preventDefault()
    this.textareaTarget.focus()
    document.execCommand("redo")
  }

  // ---- Textarea helpers -----------------------------------------------

  // Inserts at the current cursor / over the current selection.
  //
  // Uses document.execCommand("insertText") rather than setRangeText so
  // the change participates in the browser's native undo stack. execCommand
  // is officially deprecated but it's still the only way to mutate a
  // textarea while preserving undo history; it's what
  // @github/markdown-toolbar-element does internally and what every
  // editor that cares about Cmd+Z uses. The setRangeText fallback exists
  // for environments where execCommand returns false (vanishingly rare
  // on real browsers, but not zero).
  insertAtCursor(text) {
    const ta = this.textareaTarget
    ta.focus()
    const inserted = document.execCommand("insertText", false, text)
    if (inserted) return

    const start = ta.selectionStart ?? ta.value.length
    const end   = ta.selectionEnd   ?? ta.value.length
    ta.setRangeText(text, start, end, "end")
    ta.dispatchEvent(new Event("input", { bubbles: true }))
  }

  // Replaces `needle` (e.g. the upload placeholder) with `replacement`.
  // Selects the needle range explicitly so execCommand("insertText")
  // overwrites it — same undo-history benefits as insertAtCursor.
  replaceInTextarea(needle, replacement) {
    const ta = this.textareaTarget
    const idx = ta.value.indexOf(needle)
    if (idx === -1) return

    ta.focus()
    ta.setSelectionRange(idx, idx + needle.length)
    const inserted = document.execCommand("insertText", false, replacement)
    if (inserted) return

    ta.setRangeText(replacement, idx, idx + needle.length, "end")
    ta.dispatchEvent(new Event("input", { bubbles: true }))
  }

  eventHasFiles(event) {
    return event.dataTransfer && Array.from(event.dataTransfer.types || []).includes("Files")
  }

  uploadToken() {
    return Math.random().toString(36).slice(2, 10)
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }

  jsonHeaders() {
    return {
      "Content-Type":  "application/json",
      "Accept":        "application/json",
      "X-CSRF-Token":  this.csrfToken()
    }
  }
}
